/**
 * The language support policy, stated where it can be read before a scan (DEC-147).
 *
 * The report already says which languages a *particular* repository had no analyzer for.
 * That is a scan result, and it only reaches someone who has already pointed TDIA at
 * something. This card is the standing commitment: what is supported, what is committed
 * and unfinished, and what is not supported at all, regardless of what was scanned last.
 *
 * The distinction the tiers exist to preserve: "committed, not finished" and "not
 * supported" are different answers to a customer. Collapsing them into one silence is what
 * DEC-146 and DEC-147 were both written to stop.
 */
import { useQuery } from '@tanstack/react-query'
import { languagesApi } from '../../api/languages'
import type { LanguageTierName } from '../../types/api'
import { Spinner } from '../shared/Spinner'

const TIER_LABEL: Record<LanguageTierName, string> = {
  supported: 'Supported',
  committed: 'Committed',
  acknowledged: 'Not supported',
}

// Status colours, never severity colours. An unsupported language is not a finding, and
// DEC-068 reserves the severity ramp for findings.
const TIER_TONE: Record<LanguageTierName, string> = {
  supported: 'text-success',
  committed: 'text-warn',
  acknowledged: 'text-faint',
}

function displayName(language: string): string {
  const special: Record<string, string> = {
    csharp: 'C#',
    javascript: 'JavaScript',
    typescript: 'TypeScript',
    php: 'PHP',
  }
  return special[language] ?? language.charAt(0).toUpperCase() + language.slice(1)
}

export function LanguageSupportCard() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['language-support'],
    queryFn: () => languagesApi.support(),
    // The policy changes on a release, not on a scan.
    staleTime: 60 * 60 * 1000,
  })

  return (
    <div className="bg-panel border border-hairline rounded-lg shadow-card p-[18px]">
      <div className="font-semibold text-[10px] leading-none tracking-[2px] uppercase text-muted mb-[6px]">
        Language support
      </div>
      <p className="text-[11px] text-muted leading-[1.5] mb-[14px]">
        What TDIA analyses, what it has committed to and has not finished, and what it only
        detects. Files in a language it cannot analyse produce no findings, which is not the
        same as finding nothing.
      </p>

      {isLoading && <Spinner />}
      {isError && (
        <p className="text-[11px] text-muted">
          The support policy could not be loaded. It is unchanged by this failure: see
          SDD §4.12.
        </p>
      )}

      {data && (
        <ul className="flex flex-col gap-[12px]">
          {data.tiers.map((t) => (
            <li key={t.tier}>
              <div className="flex items-baseline gap-[8px] mb-[3px]">
                <span
                  className={`text-[9px] uppercase tracking-[1px] font-semibold ${TIER_TONE[t.tier]}`}
                >
                  {TIER_LABEL[t.tier]}
                </span>
                <span className="text-[11px] text-body">
                  {t.languages.length > 0
                    ? t.languages.map(displayName).join(', ')
                    : 'Any other language TDIA detects'}
                </span>
              </div>
              <div className="text-[10px] text-faint leading-[1.45]">{t.meaning}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
